import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { Button, InputNumber, Tooltip } from 'antd';
import { EditOutlined, LockOutlined } from '@ant-design/icons';
import { numberWithCommas, rateWithCommas } from 'utils/NumberCommaSeprator';
import { CountEditorRow } from './warehousingStyles';
export default function DailyCountEditor({
	entryType,
	entryId,
	value,
	uploadedOn,
	rate,
	rateLabel,
	frozen,
	frozenReason,
	canEdit,
	onSave,
}) {
	const [editing, setEditing] = useState(false);
	const [draft, setDraft] = useState(value);
	const [saving, setSaving] = useState(false);
	useEffect(() => {
		setDraft(value);
		setEditing(false);
	}, [value, entryId]);
	useEffect(() => {
		if (frozen) setEditing(false);
	}, [frozen]);

	const editable = canEdit && !frozen;
	const hasDraft = draft !== null && draft !== undefined && draft !== '';
	const isDirty = hasDraft && Number(draft) !== Number(value);

	const handleCancel = () => {
		setDraft(value);
		setEditing(false);
	};

	const handleSave = async () => {
		if (!isDirty) {
			handleCancel();
			return;
		}
		setSaving(true);
		try {
			await onSave({ entryType, entryId, count: Number(draft) });
			setEditing(false);
		} finally {
			setSaving(false);
		}
	};

	const amount = rate === undefined || rate === null ? null : Math.round(rate * Number(value));
	const unit = entryType === 'bag' ? 'bag' : 'shipment';

	return (
		<CountEditorRow>
			<div className="count-editor-controls">
				{editing ? (
					<InputNumber
						autoFocus
						min={0}
						step={1}
						precision={0}
						value={draft}
						onChange={setDraft}
						onPressEnter={handleSave}
						aria-label={`${unit} count`}
					/>
				) : (
					<div className="count-editor-value">{numberWithCommas(value ?? 0)}</div>
				)}

				<div className="count-editor-actions">
					{editing && (
						<>
							<Button size="small" disabled={saving} onClick={handleCancel}>
								Cancel
							</Button>
							<Button
								size="small"
								type="primary"
								loading={saving}
								disabled={!hasDraft}
								onClick={handleSave}
							>
								Save
							</Button>
						</>
					)}

					{!editing && editable && (
						<Button
							size="small"
							icon={<EditOutlined />}
							onClick={() => setEditing(true)}
						>
							Adjust
						</Button>
					)}

					{frozen && (
						<Tooltip title={frozenReason}>
							<span className="count-editor-frozen">
								<LockOutlined /> Frozen
							</span>
						</Tooltip>
					)}
				</div>
			</div>

			{uploadedOn && <div className="count-editor-meta">Uploaded on {uploadedOn}</div>}

			{amount !== null && (
				<div className="count-editor-meta">
					{/* The charge head is named by the operator on the project — never
					    label it with a guess when the caller has no name to give. */}
					{rateLabel ? `${rateLabel} ` : ''}@ ₹{rateWithCommas(rate)} / {unit} = ₹
					{numberWithCommas(amount)}
				</div>
			)}
		</CountEditorRow>
	);
}

DailyCountEditor.propTypes = {
	entryType: PropTypes.oneOf(['shipment', 'bag']).isRequired,
	entryId: PropTypes.number.isRequired,
	value: PropTypes.number,
	uploadedOn: PropTypes.string,
	rate: PropTypes.number,
	rateLabel: PropTypes.string,
	frozen: PropTypes.bool,
	frozenReason: PropTypes.string,
	canEdit: PropTypes.bool,
	onSave: PropTypes.func.isRequired,
};

DailyCountEditor.defaultProps = {
	value: 0,
	uploadedOn: undefined,
	rate: undefined,
	rateLabel: undefined,
	frozen: false,
	frozenReason: undefined,
	canEdit: false,
};
